import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, MapPinLine } from "@phosphor-icons/react";

export default function RoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", group: "R", loader_slots: 2 });

  const load = async () => {
    try {
      setRoutes(await api.listRoutes());
    } catch {
      toast.error("Failed to load routes");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ code: "", group: "R", loader_slots: 2 });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ code: r.code, group: r.group, loader_slots: r.loader_slots });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.code.trim()) {
      toast.error("Code required");
      return;
    }
    try {
      const payload = { ...form, loader_slots: Number(form.loader_slots) || 1 };
      if (editing) await api.updateRoute(editing.id, payload);
      else await api.createRoute(payload);
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Updated" : "Added");
    } catch {
      toast.error("Save failed");
    }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete route ${r.code}? This removes all assignments.`)) return;
    try {
      await api.deleteRoute(r.id);
      await load();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 min-h-screen">
      <header className="flex items-end justify-between border-b-2 border-black pb-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-none">ROUTE CATALOG</h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-2">
            {routes.length} ROUTES CONFIGURED
          </div>
        </div>
        <Button
          onClick={openNew}
          data-testid="add-route-btn"
          className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
        >
          <Plus size={14} weight="bold" className="mr-2" /> ADD ROUTE
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {routes.map((r) => (
          <div
            key={r.id}
            className="border-2 border-black bg-white p-4 flex flex-col hover:shadow-[4px_4px_0_0_#0A0A0A] transition-shadow"
            data-testid={`route-card-${r.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-heading font-black text-2xl tracking-tight leading-none">
                  {r.code}
                </div>
                <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-1">
                  GROUP {r.group}
                </div>
              </div>
              <MapPinLine size={20} weight="bold" className="text-zinc-400" />
            </div>
            <div className="mt-4 border-t border-zinc-200 pt-3 flex items-center justify-between">
              <div className="font-mono-plex text-[10px] tracking-widest">
                <span className="text-zinc-500">SLOTS:</span> 1 D · {r.loader_slots} L
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => openEdit(r)}
                  data-testid={`edit-route-${r.id}`}
                  className="p-1.5 border border-black hover:bg-black hover:text-white transition-colors"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
                <button
                  onClick={() => del(r)}
                  data-testid={`delete-route-${r.id}`}
                  className="p-1.5 border border-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing ? "EDIT ROUTE" : "ADD ROUTE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">CODE</label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                data-testid="route-form-code"
                className="rounded-none border-black font-mono-plex uppercase"
                placeholder="e.g. R7"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">GROUP</label>
              <div className="flex border border-black">
                {["R", "C", "F", "G", "MISC"].map((g) => (
                  <button
                    key={g}
                    onClick={() => setForm({ ...form, group: g })}
                    data-testid={`route-form-group-${g}`}
                    className={`flex-1 py-2 text-xs font-mono-plex tracking-widest transition-colors ${
                      form.group === g ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">
                LOADER SLOTS
              </label>
              <Input
                type="number"
                min={0}
                max={5}
                value={form.loader_slots}
                onChange={(e) => setForm({ ...form, loader_slots: e.target.value })}
                data-testid="route-form-slots"
                className="rounded-none border-black font-mono-plex"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              data-testid="route-form-save"
              className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
            >
              SAVE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
